/**
 * Browser-side Microsoft Entra integration: configuration resolution, an MSAL
 * app factory, and a bearer-token acquirer for the platform API.
 *
 * The API's {@link module:platform/http-client} needs a `getAccessToken()` that
 * returns a fresh access token per request. This module supplies that from MSAL:
 * it prefers a silent acquisition against the cached session, and signals — via
 * {@link EntraInteractionRequiredError} — when the caller must fall back to an
 * interactive sign-in. Token acquisition is written against a minimal interface
 * ({@link SilentTokenClient}) so it can be unit-tested without a live tenant; the
 * live sign-in handshake itself is only verifiable against real Entra.
 *
 * @module auth/entra-browser
 */

import {
  type AccountInfo,
  type AuthenticationResult,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type SilentRequest,
} from "@azure/msal-browser";

/**
 * The API's route prefix. Duplicated from `src/api/server.ts` rather than
 * imported: that module pulls in Fastify, tedious, and other Node-only
 * dependencies that must never reach the browser bundle. Keep the two in sync.
 */
const API_PREFIX = "/api/v1";

/**
 * Resolved browser Entra configuration. Every field is required to stand up the
 * real sign-in path; see {@link resolveEntraBrowserConfig} for how a partial
 * configuration is treated.
 */
export interface EntraBrowserConfig {
  /** Application (client) id of the SPA's Entra app registration. */
  clientId: string;
  /** Authority URL, e.g. an External ID (CIAM) or workforce tenant authority. */
  authority: string;
  /** Scope requested for the platform API, e.g. `api://<client-id>/access_as_user`. */
  apiScope: string;
  /** Base URL of the platform API the acquired token is presented to. */
  apiBaseUrl: string;
  /**
   * Non-standard authorities MSAL must be told to trust (required for CIAM /
   * `*.ciamlogin.com` authorities). Empty for standard login.microsoftonline.com.
   */
  knownAuthorities: string[];
  /** Redirect URI registered for the app; defaults to the window origin. */
  redirectUri?: string;
  /** Central sign-in origin for cross-origin authentication bounce flows (e.g. login.socratiq.ie). */
  loginOrigin?: string;
}

/**
 * Resolve the browser Entra configuration from an environment record (typically
 * `import.meta.env`). Taking a plain record rather than reading `import.meta`
 * directly keeps this pure and unit-testable.
 *
 * The three Entra values (client id, authority, API scope) are required. If any
 * is missing the function returns `undefined`, which the composition root reads
 * as "Entra is not configured" and renders the configuration-required screen.
 * There is no fallback runtime to degrade into, so a partial configuration stops
 * the app rather than standing up a half-wired sign-in.
 *
 * The API base URL falls back to `defaultApiBaseUrl` plus the API's route
 * prefix, which the composition root supplies as the window origin. Production
 * serves the SPA and the API from one container, so they share an origin;
 * deriving it at runtime rather than requiring it at build time is what keeps
 * one built image usable on the container app's own hostname and on a custom
 * domain without a rebuild. Vite inlines `import.meta.env` at build time, so
 * anything read from there is baked into the bundle. An explicit
 * `VITE_PLATFORM_API_URL` is used exactly as given — it names a full API root,
 * prefix included, for deployments that split the API onto its own origin.
 *
 * @param env - Environment variables, e.g. `import.meta.env`.
 * @param defaultApiBaseUrl - API base URL to use when the environment omits one.
 * @returns The resolved configuration, or `undefined` when Entra is unconfigured.
 */
export function resolveEntraBrowserConfig(
  env: Record<string, string | undefined>,
  defaultApiBaseUrl?: string,
): EntraBrowserConfig | undefined {
  const clientId = env.VITE_ENTRA_CLIENT_ID?.trim();
  let authority = env.VITE_ENTRA_AUTHORITY?.trim();
  const apiScope = env.VITE_ENTRA_API_SCOPE?.trim();
  const trimmedOrigin = defaultApiBaseUrl?.trim();
  const apiBaseUrl =
    env.VITE_PLATFORM_API_URL?.trim() ||
    (trimmedOrigin ? `${trimmedOrigin.replace(/\/+$/, "")}${API_PREFIX}` : undefined);
  if (!clientId || !authority || !apiScope || !apiBaseUrl) {
    return undefined;
  }

  // Normalize single-tenant tenant-GUID authority URLs to organizations for multi-tenant sign-in,
  // unless explicitly forced to single-tenant via VITE_ENTRA_MULTI_TENANT=false.
  if (
    env.VITE_ENTRA_MULTI_TENANT !== "false" &&
    /https:\/\/login\.microsoftonline\.com\/[0-9a-f-]{36}/i.test(authority)
  ) {
    authority = authority.replace(
      /https:\/\/login\.microsoftonline\.com\/[0-9a-f-]{36}/i,
      "https://login.microsoftonline.com/organizations",
    );
  }

  const knownAuthorities = (env.VITE_ENTRA_KNOWN_AUTHORITIES ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  const redirectUri = env.VITE_ENTRA_REDIRECT_URI?.trim() || undefined;
  const loginOrigin = env.VITE_LOGIN_ORIGIN?.trim() || undefined;

  return { clientId, authority, apiScope, apiBaseUrl, knownAuthorities, redirectUri, loginOrigin };
}

/**
 * Raised when a token cannot be obtained without user interaction — no cached
 * account, or MSAL reporting that a silent refresh needs a fresh sign-in. The
 * caller catches this to trigger an interactive login rather than surfacing a
 * raw MSAL error.
 */
export class EntraInteractionRequiredError extends Error {
  constructor(message = "Interactive sign-in is required before an access token can be acquired.") {
    super(message);
    this.name = "EntraInteractionRequiredError";
  }
}

/**
 * The subset of MSAL's `IPublicClientApplication` the token acquirer depends on.
 * Narrowing to this keeps the acquirer testable with a fake and independent of
 * MSAL's construction and initialisation lifecycle.
 */
export interface SilentTokenClient {
  initialize(): Promise<void>;
  getActiveAccount(): AccountInfo | null;
  getAllAccounts(): AccountInfo[];
  acquireTokenSilent(request: SilentRequest): Promise<AuthenticationResult>;
}

/**
 * Read a colleague's profile photo. Delegated, and tenant-wide, so it needs
 * admin consent on the app registration; without it every silent acquisition
 * fails and presence falls back to initials, which is the same thing that
 * happens for the many people who have no photo set.
 */
export const GRAPH_PHOTO_SCOPE = "https://graph.microsoft.com/User.ReadBasic.All";

/**
 * Acquire a token for a resource other than the platform API.
 *
 * A token is per-resource, so Graph needs its own silent acquisition rather
 * than a wider scope list on the API's: asking for both in one call returns a
 * token for neither.
 *
 * @param client - A silent-capable MSAL client.
 * @param scopes - The scopes to acquire, all for one resource.
 * @returns An async acquirer for those scopes.
 */
export function createScopedTokenAcquirer(
  client: SilentTokenClient,
  scopes: string[],
): () => Promise<string> {
  return async function getToken(): Promise<string> {
    await client.initialize();
    const account = client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
    if (!account) {
      throw new EntraInteractionRequiredError();
    }

    try {
      const result = await client.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        throw new EntraInteractionRequiredError();
      }
      throw error;
    }
  };
}

/**
 * Build a `getAccessToken()` that returns a fresh API access token per call.
 *
 * It targets the active account, falling back to the first cached account, and
 * acquires the token silently. When no account is present, or MSAL reports that
 * interaction is required, it raises {@link EntraInteractionRequiredError} so the
 * caller can start an interactive sign-in; any other error propagates unchanged.
 *
 * `initialize()` is awaited first, every call. `EntraAuthProvider` already
 * awaits it once during its own lifecycle, but this acquirer has no visibility
 * into whether the `client` instance it was built against is the one that
 * finished initializing — under React 18 development double-rendering, a fresh
 * `PublicClientApplication` can exist alongside the one that was actually
 * initialized, and calling `acquireTokenSilent` on the wrong one throws
 * `uninitialized_public_client_application` with no session ever reaching the
 * network. MSAL's `initialize()` caches its promise and resolves immediately on
 * a second call, so awaiting it here is cheap and removes the ordering
 * assumption entirely rather than chasing the specific race.
 *
 * @param client - A silent-capable MSAL client (a `PublicClientApplication`).
 * @param config - The resolved Entra configuration providing the API scope.
 * @returns An async `getAccessToken()` suitable for the HTTP platform runtime.
 */
export function createEntraTokenAcquirer(
  client: SilentTokenClient,
  config: EntraBrowserConfig,
): () => Promise<string> {
  return createScopedTokenAcquirer(client, [config.apiScope]);
}

/**
 * Construct the MSAL application for the SPA. The token cache lives in
 * `sessionStorage`, not `localStorage`, so it is scoped to the browser session:
 * a shared machine does not leave one user's tokens available to the next, and
 * the cache stays isolated from the application's own storage.
 *
 * @param config - The resolved Entra configuration.
 * @returns An uninitialised `PublicClientApplication`; call `initialize()` before use.
 */
export function createEntraApp(config: EntraBrowserConfig): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      knownAuthorities: config.knownAuthorities,
      redirectUri: config.redirectUri ?? window.location.origin,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
  });
}
